import { UserVoteType } from './reaction';
import { DeviceType } from '@/hooks/useDeviceType';
import { DocumentType } from '@/services/reaction.service';
import { FeedEntry } from './feed';
import { Work } from './work';
import { TabType } from '@/components/work/WorkTabs';
import {
  mapAppContentTypeToApiType,
  mapAppFeedContentTypeToApiType,
} from '@/utils/contentTypeMapping';

interface UserContext {
  user_id?: string;
  author_id?: string;
  editor?: boolean;
  moderator?: boolean;
}

interface BaseContext {
  device_type: DeviceType;
}

interface RelatedWork {
  id: string;
  content_type: DocumentType;
  unified_document_id?: string;
}

export type FeedSource =
  | 'home'
  | 'earn'
  | 'fund'
  | 'journal'
  | 'topic'
  | 'author'
  | 'search'
  | 'list'
  | 'unknown';

/**
 * Extracts the unified document ID from a feed entry.
 * Checks entry.content.unifiedDocumentId first, then falls back to entry.relatedWork?.unifiedDocumentId.
 *
 * @param entry - The feed entry to extract the unified document ID from
 * @returns The unified document ID as a string, or empty string if not found
 */
export function getUnifiedDocumentId(entry: FeedEntry): string {
  return entry.content.unifiedDocumentId || entry.relatedWork?.unifiedDocumentId?.toString() || '';
}

// 1. Vote Action
export interface VoteActionEvent extends UserContext, BaseContext {
  vote_type: UserVoteType;
  related_work?: RelatedWork;
}

// 2. Feed Item Clicked
export interface FeedItemClickedEvent extends UserContext, BaseContext {
  feed_position: number;
  feed_source: FeedSource;
  feed_tab: string;
  related_work?: RelatedWork;
  recommendation_id?: string | null;
  feed_ordering?: string;
  impression?: string[];
}

// 2b. Feed Item Abstract Expanded
export interface FeedItemAbstractExpandedEvent extends UserContext, BaseContext {
  feed_position: number;
  feed_source: FeedSource;
  feed_tab: string;
  related_work?: RelatedWork;
  recommendation_id?: string | null;
  feed_ordering?: string;
  impression?: string[];
}

// 3. Work Document Viewed
export interface WorkDocumentViewedEvent extends UserContext {
  related_work?: RelatedWork;
  tab?: string;
}

// 4. Document Tab Clicked
export interface DocumentTabClickedEvent extends UserContext, BaseContext {
  clicked_tab: TabType;
  related_work?: RelatedWork;
}

/**
 * Builds the payload for a feed item click event
 * @param entry - The feed entry that was clicked
 * @param options - Options for building the payload
 * @returns The FeedItemClickedEvent payload
 */
export function buildPayloadForFeedItemClick(
  entry: FeedEntry,
  options: {
    feedPosition?: number;
    feedSource: FeedSource;
    feedTab: string;
    deviceType: DeviceType;
    feedOrdering?: string;
    impression?: string[];
  }
): FeedItemClickedEvent {
  const { feedPosition = 1, feedSource, feedTab, deviceType, feedOrdering, impression } = options;

  const payload: FeedItemClickedEvent = {
    device_type: deviceType,
    feed_position: feedPosition,
    feed_source: feedSource,
    feed_tab: feedTab,
    related_work: {
      id:
        'relatedDocumentId' in entry.content && entry.content.relatedDocumentId
          ? entry.content.relatedDocumentId.toString()
          : entry.content.id?.toString() || '',
      content_type:
        'relatedDocumentContentType' in entry.content && entry.content.relatedDocumentContentType
          ? mapAppContentTypeToApiType(entry.content.relatedDocumentContentType)
          : mapAppFeedContentTypeToApiType(entry.content.contentType),
      unified_document_id: getUnifiedDocumentId(entry),
    },
    recommendation_id: entry.recommendationId,
    // Include feed ordering if provided
    ...(feedOrdering && { feed_ordering: feedOrdering }),
    // Include impressions if provided
    ...(impression && impression.length > 0 && { impression }),
  };

  return payload;
}

/**
 * Builds the payload for a feed item abstract expansion event
 * @param entry - The feed entry whose abstract was expanded
 * @param options - Options for building the payload
 * @returns The FeedItemAbstractExpandedEvent payload
 */
export function buildPayloadForFeedItemAbstractExpanded(
  entry: FeedEntry,
  options: {
    feedPosition?: number;
    feedSource: FeedSource;
    feedTab: string;
    deviceType: DeviceType;
    feedOrdering?: string;
    impression?: string[];
  }
): FeedItemAbstractExpandedEvent {
  const { feedPosition = 1, feedSource, feedTab, deviceType, feedOrdering, impression } = options;

  const payload: FeedItemAbstractExpandedEvent = {
    device_type: deviceType,
    feed_position: feedPosition,
    feed_source: feedSource,
    feed_tab: feedTab,
    related_work: {
      id:
        'relatedDocumentId' in entry.content && entry.content.relatedDocumentId
          ? entry.content.relatedDocumentId.toString()
          : entry.content.id?.toString() || '',
      content_type:
        'relatedDocumentContentType' in entry.content && entry.content.relatedDocumentContentType
          ? mapAppContentTypeToApiType(entry.content.relatedDocumentContentType)
          : mapAppFeedContentTypeToApiType(entry.content.contentType),
      unified_document_id: getUnifiedDocumentId(entry),
    },
    recommendation_id: entry.recommendationId,
    // Include feed ordering if provided
    ...(feedOrdering && { feed_ordering: feedOrdering }),
    // Include impressions if provided
    ...(impression && impression.length > 0 && { impression }),
  };

  return payload;
}

/**
 * Builds the payload for a document tab click event
 * @param work - The work document where the tab was clicked
 * @param options - Options for building the payload
 * @returns The DocumentTabClickedEvent payload
 */
export function buildPayloadForDocumentTabClick(
  work: Work,
  options: {
    clickedTab: TabType;
    deviceType: DeviceType;
  }
): DocumentTabClickedEvent {
  const { clickedTab, deviceType } = options;

  const payload: DocumentTabClickedEvent = {
    device_type: deviceType,
    clicked_tab: clickedTab,
    related_work: {
      id: work.id.toString(),
      content_type: mapAppContentTypeToApiType(work.contentType),
      unified_document_id: work.unifiedDocumentId?.toString(),
    },
  };

  return payload;
}
